import { useEffect, useRef, useState, useCallback } from "react";
import Player from "@vimeo/player";
import { Progress } from "@/components/ui/progress";
import { Play, Pause, Volume2, VolumeX, RotateCcw, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface VideoPlayerProps {
  videoUrl: string;
  requiredWatchPercent?: number;
  onProgress: (watchedPercent: number) => void;
  onComplete: () => void;
  initialProgress?: number;
  hideProgressUI?: boolean;
}

// Detect if URL is a Loom embed URL
const isLoomUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  return url.includes('loom.com');
};

// Detect if URL is a Vimeo URL
const isVimeoUrl = (url: string | undefined | null): boolean => {
  if (!url) return false;
  return url.includes('vimeo.com');
};

// Loom Player Component (iframe-based with simulated progress tracking)
function LoomPlayer({
  videoUrl,
  requiredWatchPercent = 95,
  onProgress,
  onComplete,
  initialProgress = 0,
}: VideoPlayerProps) {
  const [watchedPercent, setWatchedPercent] = useState(initialProgress);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  
  // Estimate video duration (Loom doesn't provide easy API access)
  // We'll simulate progress based on time watched
  const estimatedDuration = 300; // 5 minutes default estimate
  
  const handlePlay = useCallback(() => {
    if (isPlaying) return;
    setIsPlaying(true);
    startTimeRef.current = Date.now();
    
    // Track progress every second
    intervalRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const newPercent = Math.min(100, (elapsed / estimatedDuration) * 100 + watchedPercent);
      
      setWatchedPercent(newPercent);
      onProgress(newPercent);
      
      if (newPercent >= requiredWatchPercent) {
        onComplete();
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }
    }, 1000);
  }, [isPlaying, watchedPercent, onProgress, onComplete, requiredWatchPercent]);

  useEffect(() => {
    // Auto-start progress tracking when iframe loads
    const timer = setTimeout(() => {
      handlePlay();
    }, 2000);
    
    return () => {
      clearTimeout(timer);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Check for completion
  useEffect(() => {
    if (watchedPercent >= requiredWatchPercent) {
      onComplete();
    }
  }, [watchedPercent, requiredWatchPercent, onComplete]);

  const isComplete = watchedPercent >= requiredWatchPercent;

  // Extract embed URL - ensure it's the embed format
  const embedUrl = videoUrl.includes('/embed/') 
    ? videoUrl 
    : videoUrl.replace('loom.com/share/', 'loom.com/embed/');

  return (
    <div className="w-full space-y-4">
      {/* Video Container */}
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
        <iframe
          src={embedUrl}
          frameBorder="0"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
          allow="autoplay; fullscreen"
        />
      </div>

      {/* Progress Status */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Progress:</span>
          <Progress value={watchedPercent} className="w-32 h-2" />
          <span className="font-medium">{Math.round(watchedPercent)}%</span>
        </div>
        
        {isComplete ? (
          <span className="text-green-600 font-medium">✓ Complete</span>
        ) : (
          <span className="text-muted-foreground">
            Watch {requiredWatchPercent}% to continue
          </span>
        )}
      </div>
    </div>
  );
}

// Vimeo Player Component with seek-based skip prevention
function VimeoPlayer({
  videoUrl,
  requiredWatchPercent = 95,
  onProgress,
  onComplete,
  initialProgress = 0,
  hideProgressUI = false,
}: VideoPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [highestWatched, setHighestWatched] = useState(initialProgress);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [playerError, setPlayerError] = useState<string | null>(null);
  
  // Track highest watched time in seconds (for skip prevention)
  const highestWatchedSecondsRef = useRef(0);
  // Flag to prevent our own programmatic seeks from triggering enforcement
  const isProgrammaticSeekRef = useRef(false);
  
  const highestWatchedRef = useRef(initialProgress);
  const onProgressRef = useRef(onProgress);
  const onCompleteRef = useRef(onComplete);
  
  useEffect(() => {
    onProgressRef.current = onProgress;
    onCompleteRef.current = onComplete;
  }, [onProgress, onComplete]);

  // Build a canonical Vimeo player URL - unlisted videos MUST use full URL with hash
  const getVimeoPlayableUrl = (input: string): string => {
    // Decode HTML entities (e.g., &amp; -> &)
    const decoded = input.replace(/&amp;/g, "&");

    // If someone pasted full embed HTML, extract the iframe src
    const iframeSrcMatch = decoded.match(/<iframe[^>]+src=["']([^"']+)["']/i);
    const rawUrl = (iframeSrcMatch?.[1] ?? decoded).trim();

    const getQueryParam = (url: string, key: string): string | null => {
      try {
        return new URL(url, "https://vimeo.com").searchParams.get(key);
      } catch {
        return null;
      }
    };

    // player.vimeo.com/video/{id}?h={hash}
    const playerMatch = rawUrl.match(/player\.vimeo\.com\/video\/(\d+)/);
    if (playerMatch) {
      const id = playerMatch[1];
      const h = getQueryParam(rawUrl, "h");
      return h
        ? `https://player.vimeo.com/video/${id}?h=${h}`
        : `https://player.vimeo.com/video/${id}`;
    }

    // vimeo.com/{id}/{hash} (unlisted path format)
    const unlistedPathMatch = rawUrl.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/);
    if (unlistedPathMatch) {
      const id = unlistedPathMatch[1];
      const h = unlistedPathMatch[2];
      return `https://player.vimeo.com/video/${id}?h=${h}`;
    }

    // vimeo.com/{id}?h={hash} or vimeo.com/{id}
    const vimeoIdMatch = rawUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoIdMatch) {
      const id = vimeoIdMatch[1];
      const h = getQueryParam(rawUrl, "h");
      return h
        ? `https://player.vimeo.com/video/${id}?h=${h}`
        : `https://player.vimeo.com/video/${id}`;
    }

    // Just a numeric ID
    if (/^\d+$/.test(rawUrl)) {
      return `https://player.vimeo.com/video/${rawUrl}`;
    }

    // Fallback: return as-is
    return rawUrl;
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear any previous iframe remnants (StrictMode double-mount fix)
    containerRef.current.innerHTML = '';

    const playableUrl = getVimeoPlayableUrl(videoUrl);
    console.log("[VimeoPlayer] Initializing with URL:", playableUrl);

    const player = new Player(containerRef.current, {
      url: playableUrl as any,
      responsive: true,
      controls: false,
      autoplay: false,
      muted: false,
    });

    playerRef.current = player;

    let pollingInterval: ReturnType<typeof setInterval> | null = null;
    let isDestroyed = false;

    // Polling runs continuously after ready - does NOT depend on play/pause events
    const startPolling = () => {
      if (pollingInterval || isDestroyed) return;
      console.log("[VimeoPlayer] Starting polling");
      
      pollingInterval = setInterval(async () => {
        if (!playerRef.current || isDestroyed) return;
        try {
          const [time, dur, paused] = await Promise.all([
            playerRef.current.getCurrentTime(),
            playerRef.current.getDuration(),
            playerRef.current.getPaused(),
          ]);

          if (isDestroyed) return;

          setCurrentTime(time);
          if (dur > 0) setDuration(dur);
          setIsPlaying(!paused);

          // Only update highest watched when actually playing
          if (!paused && time > highestWatchedSecondsRef.current) {
            highestWatchedSecondsRef.current = time;
          }

          // Calculate and report progress percentage (only when playing)
          if (!paused && dur > 0) {
            const percent = (highestWatchedSecondsRef.current / dur) * 100;
            if (percent > highestWatchedRef.current) {
              highestWatchedRef.current = percent;
              setHighestWatched(percent);
              onProgressRef.current(percent);
            }
          }
        } catch (err) {
          // Player may have been destroyed
          if (!isDestroyed) {
            console.warn("[VimeoPlayer] Polling error:", err);
          }
        }
      }, 500);
    };

    const stopPolling = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
      }
    };

    player
      .ready()
      .then(async () => {
        if (isDestroyed) return;
        console.log("[VimeoPlayer] Player ready");
        setIsReady(true);
        
        const dur = await player.getDuration();
        if (isDestroyed) return;
        setDuration(dur);
        console.log("[VimeoPlayer] Duration:", dur);

        // Sync muted state with Vimeo player's actual state and try to unmute
        try {
          const actualMuted = await player.getMuted();
          console.log("[VimeoPlayer] Actual muted state from Vimeo:", actualMuted);
          if (actualMuted) {
            // Try to unmute - this will work after user interaction
            await player.setMuted(false);
            setIsMuted(false);
            console.log("[VimeoPlayer] Attempted to unmute player");
          }
        } catch (e) {
          console.log("[VimeoPlayer] Could not sync/unmute:", e);
        }

        // Resume from saved progress
        if (initialProgress > 0 && dur > 0) {
          const resumeTime = (initialProgress / 100) * dur;
          highestWatchedSecondsRef.current = resumeTime;
          highestWatchedRef.current = initialProgress;
          setHighestWatched(initialProgress);
          isProgrammaticSeekRef.current = true;
          await player.setCurrentTime(resumeTime);
          isProgrammaticSeekRef.current = false;
        }

        // Start polling immediately after ready - this is the primary time tracker
        startPolling();
      })
      .catch((err) => {
        if (isDestroyed) return;
        console.error("[VimeoPlayer] Ready failed:", err);
        const errMsg = String(err?.message || err || '');
        if (errMsg.includes('not found') || errMsg.includes('404')) {
          setPlayerError(
            "Video not found. For unlisted videos, ensure the URL includes the privacy hash."
          );
        } else {
          setPlayerError(
            "Failed to load video. Please check embedding is enabled for this video."
          );
        }
      });

    // Timeupdate as optimization (may fire more frequently than polling)
    player.on('timeupdate', (data) => {
      if (isDestroyed) return;
      setCurrentTime(data.seconds);
      
      // Update highest watched seconds
      if (data.seconds > highestWatchedSecondsRef.current) {
        highestWatchedSecondsRef.current = data.seconds;
      }
      
      // Calculate and report progress percentage
      if (data.duration > 0) {
        const percent = (highestWatchedSecondsRef.current / data.duration) * 100;
        if (percent > highestWatchedRef.current) {
          highestWatchedRef.current = percent;
          setHighestWatched(percent);
          onProgressRef.current(percent);
        }
      }
    });

    // Anti-skip enforcement via seeked event
    player.on('seeked', async (data) => {
      if (isDestroyed || isProgrammaticSeekRef.current) return;
      if (requiredWatchPercent === 0) return;
      
      const allowedMax = highestWatchedSecondsRef.current + 2;
      
      if (data.seconds > allowedMax) {
        console.log('[VimeoPlayer] Skip blocked:', data.seconds, '> allowed max', allowedMax);
        isProgrammaticSeekRef.current = true;
        await player.setCurrentTime(highestWatchedSecondsRef.current);
        isProgrammaticSeekRef.current = false;
        
        const paused = await player.getPaused();
        if (paused) {
          player.play().catch(() => {});
        }
      }
    });

    // Play/pause events as backup state sync
    player.on('play', () => {
      if (isDestroyed) return;
      console.log('[VimeoPlayer] Play event');
      setIsPlaying(true);
    });
    
    player.on('pause', () => {
      if (isDestroyed) return;
      console.log('[VimeoPlayer] Pause event');
      setIsPlaying(false);
    });
    
    player.on('ended', () => {
      if (isDestroyed) return;
      console.log('[VimeoPlayer] Ended event');
      setIsPlaying(false);
      if (highestWatchedRef.current >= requiredWatchPercent) {
        onCompleteRef.current();
      }
    });

    player.on('error', (err) => {
      if (isDestroyed) return;
      console.error('[VimeoPlayer] Error:', err);
      setPlayerError('This video could not play here. It may be blocked from showing on this page.');
    });

    return () => {
      isDestroyed = true;
      stopPolling();
      player.destroy().catch(() => {});
      playerRef.current = null;
    };
  }, [videoUrl]);
  useEffect(() => {
    if (highestWatched >= requiredWatchPercent) {
      onComplete();
    }
  }, [highestWatched, requiredWatchPercent, onComplete]);

  const togglePlay = useCallback(async () => {
    const player = playerRef.current;
    if (!player) return;

    try {
      const paused = await player.getPaused();

      if (!paused) {
        await player.pause();
        setIsPlaying(false);
        return;
      }

      // When user clicks play, try to unmute first (user interaction enables unmute)
      try {
        await player.setMuted(false);
        setIsMuted(false);
      } catch (e) {
        // Ignore unmute failures
      }

      try {
        await player.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Video play failed:', error);
        // If play fails due to autoplay policy, try playing muted first
        try {
          await player.setMuted(true);
          setIsMuted(true);
          await player.play();
          setIsPlaying(true);
        } catch (mutedError) {
          console.error('Even muted play failed:', mutedError);
          setPlayerError('Unable to play video. Please try refreshing the page.');
        }
      }
    } catch (err) {
      console.error('Video toggle failed:', err);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!playerRef.current) return;
    playerRef.current.setMuted(!isMuted);
    setIsMuted(!isMuted);
  }, [isMuted]);

  // Seek backward (always allowed)
  const seekBackward = useCallback(async () => {
    if (!playerRef.current) return;
    const current = await playerRef.current.getCurrentTime();
    const newTime = Math.max(0, current - 15);
    isProgrammaticSeekRef.current = true;
    await playerRef.current.setCurrentTime(newTime);
    isProgrammaticSeekRef.current = false;
  }, []);

  // Seek forward (capped at highest watched)
  const seekForward = useCallback(async () => {
    if (!playerRef.current) return;
    const current = await playerRef.current.getCurrentTime();
    const maxAllowed = highestWatchedSecondsRef.current;
    const newTime = Math.min(maxAllowed, current + 15);
    isProgrammaticSeekRef.current = true;
    await playerRef.current.setCurrentTime(newTime);
    isProgrammaticSeekRef.current = false;
  }, []);

  // Handle timeline click (capped at highest watched)
  const handleTimelineClick = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    if (!playerRef.current || duration === 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickPercent = (e.clientX - rect.left) / rect.width;
    const targetTime = clickPercent * duration;
    
    // Cap at highest watched position for forward seeks
    const current = await playerRef.current.getCurrentTime();
    let allowedTime = targetTime;
    if (targetTime > current) {
      allowedTime = Math.min(targetTime, highestWatchedSecondsRef.current);
    }
    
    isProgrammaticSeekRef.current = true;
    await playerRef.current.setCurrentTime(allowedTime);
    isProgrammaticSeekRef.current = false;
  }, [duration]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isComplete = highestWatched >= requiredWatchPercent;

  return (
    <div className="w-full space-y-4">
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
        {/* Layer A: Vimeo iframe container */}
        <div ref={containerRef} className="absolute inset-0 z-0" />
        
        {/* Click overlay removed - let iframe receive clicks directly for audio activation */}
        
        {playerError && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/90">
            <div className="text-center p-4">
              <p className="text-red-400 mb-2">{playerError}</p>
              <p className="text-white/60 text-sm">
                If this persists, the video may need its Vimeo privacy settings updated to allow embedding.
              </p>
            </div>
          </div>
        )}
        
        {/* Layer C: Custom control bar - pointer-events on buttons only */}
        {isReady && !playerError && (
          <div 
            className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent p-4 pointer-events-none"
          >
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Rewind 15s button */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 pointer-events-auto h-8 w-8 sm:h-10 sm:w-10"
                onClick={seekBackward}
                title="Rewind 15 seconds"
              >
                <RotateCcw className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>

              {/* Play/Pause button */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 pointer-events-auto h-8 w-8 sm:h-10 sm:w-10"
                onClick={togglePlay}
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <Play className="h-5 w-5 sm:h-6 sm:w-6" />
                )}
              </Button>

              {/* Forward 15s button (capped at watched position) */}
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 pointer-events-auto h-8 w-8 sm:h-10 sm:w-10"
                onClick={seekForward}
                title="Forward 15 seconds (up to watched position)"
              >
                <RotateCw className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              
              {/* Interactive Timeline */}
              <div 
                className="flex-1 relative h-6 cursor-pointer pointer-events-auto group"
                onClick={handleTimelineClick}
              >
                {/* Track background */}
                <div className="absolute top-1/2 -translate-y-1/2 w-full h-1.5 bg-white/30 rounded-full overflow-hidden">
                  {/* Watched zone (green) - shows how far user has watched */}
                  <div 
                    className="absolute left-0 top-0 h-full bg-green-500/60 rounded-full transition-all"
                    style={{ width: `${duration > 0 ? (highestWatched / 100) * 100 : 0}%` }}
                  />
                  {/* Current position (white) */}
                  <div 
                    className="absolute left-0 top-0 h-full bg-white rounded-full transition-all"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
                {/* Scrubber handle */}
                <div 
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ left: `calc(${duration > 0 ? (currentTime / duration) * 100 : 0}% - 6px)` }}
                />
              </div>
              
              <span className="text-white text-xs sm:text-sm font-mono whitespace-nowrap">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
              
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20 pointer-events-auto h-8 w-8 sm:h-10 sm:w-10"
                onClick={toggleMute}
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {!hideProgressUI && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs sm:text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Progress:</span>
            <Progress value={highestWatched} className="w-20 sm:w-32 h-2" />
            <span className="font-medium">{Math.round(highestWatched)}%</span>
          </div>
          
          {isComplete ? (
            <span className="text-green-600 font-medium">✓ Complete</span>
          ) : (
            <span className="text-muted-foreground">
              Watch {requiredWatchPercent}% to continue
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Main VideoPlayer component that auto-detects the platform
export function VideoPlayer(props: VideoPlayerProps) {
  const { videoUrl } = props;
  
  // Handle missing/undefined video URL
  if (!videoUrl) {
    return (
      <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
        <p className="text-muted-foreground">No video available</p>
      </div>
    );
  }
  
  if (isLoomUrl(videoUrl)) {
    return <LoomPlayer {...props} />;
  }
  
  if (isVimeoUrl(videoUrl)) {
    return <VimeoPlayer {...props} />;
  }
  
  // Fallback: try to render as iframe
  return <LoomPlayer {...props} />;
}
